import { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Upload, Download, CheckCircle, XCircle, AlertTriangle, ChevronDown, FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BulkUpload = ({ 
  title, 
  downloadTemplate, 
  uploadEndpoint, 
  confirmEndpoint, 
  onSuccess,
  getAuthHeader,
  API,
  additionalMenuItems = [], // Array of { label, icon, onClick }
  useDropdown = false // Enable dropdown mode
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setUploadResult(null);
    setPreviewData(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}${uploadEndpoint}`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        setPreviewData(result.preview_data);
        toast.success(`${result.total_count} records ready for upload`);
      } else {
        setUploadResult(result);
        toast.error('Validation errors found. Please review and correct.');
      }
    } catch (error) {
      toast.error('Failed to upload file');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}${confirmEndpoint}`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(previewData)
      });

      const result = await response.json();
      
      if (result.success) {
        const skipped = result.skipped_count || 0;
        if (skipped > 0) {
          // Compose readable skipped list (up to first 5 for toast)
          const details = (result.skipped_duplicates || [])
            .slice(0, 5)
            .map(d => `${d.name || '-'} → matched existing "${d.existing_name}" (${d.matched_by})`)
            .join(' · ');
          toast.warning(
            `Uploaded ${result.created_count} records. Skipped ${skipped} duplicate(s).${details ? '\n' + details : ''}`,
            { duration: 8000 }
          );
        } else {
          toast.success(`Successfully uploaded ${result.created_count} records`);
        }
        setDialogOpen(false);
        setFile(null);
        setPreviewData(null);
        setUploadResult(null);
        if (onSuccess) onSuccess();
      } else {
        toast.error('Failed to save records');
      }
    } catch (error) {
      toast.error('Failed to confirm upload');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setFile(null);
    setUploadResult(null);
    setPreviewData(null);
  };

  // Dropdown mode with consolidated menu
  if (useDropdown) {
    return (
      <>
        <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <PopoverTrigger asChild>
            <Button
              className="bg-gradient-to-r from-lime-600 to-green-600 text-xs px-2 py-1.5 h-8 whitespace-nowrap"
              data-testid="bulk-actions-btn"
            >
              <Upload className="w-3 h-3 mr-1" />
              Bulk Upload
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-52 p-1" align="end">
            <Button
              variant="ghost"
              className="w-full justify-start text-xs h-8"
              onClick={() => { downloadTemplate(); setDropdownOpen(false); }}
              data-testid="download-template-btn"
            >
              <Download className="w-3 h-3 mr-2" />
              Download Template
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start text-xs h-8"
              onClick={() => { setDialogOpen(true); setDropdownOpen(false); }}
              data-testid="upload-data-btn"
            >
              <FileSpreadsheet className="w-3 h-3 mr-2" />
              Upload Data
            </Button>
            {additionalMenuItems.map((item, idx) => (
              <Button
                key={idx}
                variant="ghost"
                className="w-full justify-start text-xs h-8"
                onClick={() => { item.onClick(); setDropdownOpen(false); }}
                data-testid={item.testId || `menu-item-${idx}`}
              >
                {item.icon}
                {item.label}
              </Button>
            ))}
          </PopoverContent>
        </Popover>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetDialog(); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>
                Upload an Excel file with {title.toLowerCase()}. Download the template to see the required format.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* File Upload */}
              {!previewData && (
                <div className="space-y-4">
                  <div>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-lime-50 file:text-lime-700 hover:file:bg-lime-100"
                    />
                  </div>
                  <Button onClick={handleUpload} disabled={!file || loading}>
                    {loading ? 'Processing...' : 'Upload & Preview'}
                  </Button>
                </div>
              )}

              {/* Preview Data */}
              {previewData && previewData.length > 0 && (
                <div className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      Preview: {previewData.length} records found. Review and confirm to save.
                    </AlertDescription>
                  </Alert>
                  
                  <div className="max-h-96 overflow-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {Object.keys(previewData[0]).slice(0, 5).map(key => (
                            <TableHead key={key}>{key}</TableHead>
                          ))}
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.slice(0, 10).map((row, idx) => (
                          <TableRow key={idx}>
                            {Object.values(row).slice(0, 5).map((val, i) => (
                              <TableCell key={i}>{String(val || '-')}</TableCell>
                            ))}
                            <TableCell>
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-800">
                                Pending
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {previewData.length > 10 && (
                    <p className="text-sm text-gray-500">
                      Showing 10 of {previewData.length} records
                    </p>
                  )}
                  
                  <div className="flex gap-2">
                    <Button onClick={handleConfirm} disabled={loading}>
                      {loading ? 'Saving...' : `Confirm & Save ${previewData.length} Records`}
                    </Button>
                    <Button variant="outline" onClick={resetDialog}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Upload Result */}
              {uploadResult && (
                <Alert className={uploadResult.success ? 'border-green-500' : 'border-red-500'}>
                  {uploadResult.success ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <AlertDescription>
                    {uploadResult.message}
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <ul className="mt-2 list-disc list-inside">
                        {uploadResult.errors.slice(0, 5).map((err, i) => (
                          <li key={i} className="text-red-600">{err}</li>
                        ))}
                      </ul>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Original mode with separate buttons
  return (
    <>
      <div className="flex gap-1">
        <Button
          variant="outline"
          onClick={downloadTemplate}
          className="text-xs px-2 py-1.5 h-8 whitespace-nowrap"
        >
          <Download className="w-3 h-3 mr-1" />
          Download Template
        </Button>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-gradient-to-r from-lime-600 to-green-600 text-xs px-2 py-1.5 h-8 whitespace-nowrap"
        >
          <Upload className="w-3 h-3 mr-1" />
          Bulk Upload
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetDialog(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              Upload an Excel file with {title.toLowerCase()}. Download the template to see the required format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Upload */}
            {!previewData && (
              <div className="space-y-4">
                <div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                  />
                </div>

                {file && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>{file.name}</span>
                  </div>
                )}

                {uploadResult && !uploadResult.success && (
                  <div className="space-y-3">
                    {uploadResult.existing_plates && uploadResult.existing_plates.length > 0 && (
                      <Alert className="border-orange-500 bg-orange-50">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                        <AlertDescription>
                          <p className="font-semibold text-orange-900 mb-2">Existing Records Found:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {uploadResult.existing_plates.map((item, idx) => (
                              <li key={idx} className="text-orange-800">{item}</li>
                            ))}
                          </ul>
                          <p className="mt-2 text-sm text-orange-700">Please remove these rows from your Excel file.</p>
                        </AlertDescription>
                      </Alert>
                    )}

                    {uploadResult.existing_ids && uploadResult.existing_ids.length > 0 && (
                      <Alert className="border-orange-500 bg-orange-50">
                        <AlertTriangle className="w-4 h-4 text-orange-600" />
                        <AlertDescription>
                          <p className="font-semibold text-orange-900 mb-2">Existing Records Found:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {uploadResult.existing_ids.map((item, idx) => (
                              <li key={idx} className="text-orange-800">{item}</li>
                            ))}
                          </ul>
                          <p className="mt-2 text-sm text-orange-700">Please remove these rows from your Excel file.</p>
                        </AlertDescription>
                      </Alert>
                    )}

                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <Alert className="border-red-500 bg-red-50">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <AlertDescription>
                          <p className="font-semibold text-red-900 mb-2">Validation Errors:</p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {uploadResult.errors.map((error, idx) => (
                              <li key={idx} className="text-red-800">{error}</li>
                            ))}
                          </ul>
                          <p className="mt-2 text-sm text-red-700">Please correct these errors and upload again.</p>
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="text-sm text-slate-600">
                      <p>Valid records: {uploadResult.valid_count} / {uploadResult.total_count}</p>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleUpload}
                  disabled={!file || loading}
                  className="w-full bg-gradient-to-r from-lime-600 to-green-600"
                >
                  {loading ? 'Validating...' : 'Validate & Preview'}
                </Button>
              </div>
            )}

            {/* Preview Data */}
            {previewData && (
              <div className="space-y-4">
                <Alert className="border-green-500 bg-green-50">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <AlertDescription>
                    <p className="font-semibold text-green-900">Validation Successful!</p>
                    <p className="text-sm text-green-700 mt-1">
                      {previewData.length} records are ready to be uploaded. Review the preview below and click Confirm to proceed.
                    </p>
                  </AlertDescription>
                </Alert>

                <div className="max-h-96 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {Object.keys(previewData[0] || {}).map((key) => (
                          <TableHead key={key} className="text-xs whitespace-nowrap">
                            {key.replace(/_/g, ' ').toUpperCase()}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.map((row, idx) => (
                        <TableRow key={idx}>
                          {Object.values(row).map((value, vidx) => (
                            <TableCell key={vidx} className="text-xs whitespace-nowrap">
                              {value?.toString() || '-'}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={resetDialog}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="bg-gradient-to-r from-lime-600 to-green-600"
                  >
                    {loading ? 'Uploading...' : `Confirm & Upload ${previewData.length} Records`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkUpload;
